/**
 * CNESFilters Component - Search filters and actions
 */
import React from 'react';
import { Search, RefreshCw, Download, FileSpreadsheet } from 'lucide-react';
import type { Filtros, SortConfig } from '../../hooks/useCNESConsulta';

interface CNESFiltersProps {
  filtros: Filtros;
  setFiltros: React.Dispatch<React.SetStateAction<Filtros>>;
  buscaExata: boolean;
  setBuscaExata: React.Dispatch<React.SetStateAction<boolean>>;
  sortConfigs: SortConfig[];
  buscaRealizada: boolean;
  resultadosCount: number;
  onBuscar: () => void;
  onAtualizar: () => void;
  onLimpar: () => void;
  onExportar: () => void;
  onExportarTudo: () => void;
}

export const CNESFilters: React.FC<CNESFiltersProps> = ({
  filtros,
  setFiltros,
  buscaExata,
  setBuscaExata,
  sortConfigs,
  buscaRealizada,
  resultadosCount,
  onBuscar,
  onAtualizar,
  onLimpar,
  onExportar,
  onExportarTudo,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Search className="w-5 h-5 text-green-600" />
        Filtros de Busca
      </h2>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome do Hospital
          </label>
          <input
            type="text"
            value={filtros.nome}
            onChange={(e) => setFiltros({ ...filtros, nome: e.target.value })}
            placeholder="Ex: Hospital das Clínicas"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cidade
          </label>
          <input
            type="text"
            value={filtros.cidade}
            onChange={(e) => setFiltros({ ...filtros, cidade: e.target.value })}
            placeholder="Ex: São Paulo"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estado (UF)
          </label>
          <input
            type="text"
            value={filtros.estado}
            onChange={(e) => setFiltros({ ...filtros, estado: e.target.value.toUpperCase() })}
            placeholder="Ex: SP"
            maxLength={2}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent uppercase"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Código CNES
          </label>
          <input
            type="text"
            value={filtros.cnes}
            onChange={(e) => setFiltros({ ...filtros, cnes: e.target.value })}
            placeholder="Ex: 2077531"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Shift+Click hint bar */}
      {sortConfigs.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          💡 <strong>Dica:</strong> Mantenha <kbd className="px-2 py-1 bg-blue-100 rounded">Shift</kbd> pressionado e clique em outras colunas para ordenar por até 3 critérios
        </div>
      )}

      <div className="flex gap-3 flex-wrap items-center">
        {/* Left side buttons */}
        <button
          onClick={() => setBuscaExata(!buscaExata)}
          className={`px-4 py-2.5 rounded-lg font-semibold transition-colors ${
            buscaExata
              ? 'bg-orange-100 text-orange-700 border-2 border-orange-400'
              : 'bg-gray-100 text-gray-700 border-2 border-gray-300'
          }`}
          title={buscaExata ? 'Busca Exata Ativada' : 'Busca Parcial Ativada'}
        >
          {buscaExata ? '🎯 Exata' : '🔍 Parcial'}
        </button>

        <button
          onClick={onBuscar}
          className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
        >
          <Search className="w-5 h-5" />
          Buscar
        </button>

        <button
          onClick={onLimpar}
          className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors"
        >
          Limpar
        </button>

        {/* Right side buttons */}
        <div className="flex gap-3 ml-auto">
          {buscaRealizada && (
            <button
              onClick={onAtualizar}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Recarregar busca atual"
            >
              <RefreshCw className="w-4 h-4" />
              Atualizar
            </button>
          )}

          {resultadosCount > 0 && (
            <button
              onClick={onExportar}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
            >
              <Download className="w-5 h-5" />
              Exportar CSV
            </button>
          )}

          <button
            onClick={onExportarTudo}
            className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold transition-colors"
            title="Exportar TODOS os hospitais (não apenas os filtrados)"
          >
            <FileSpreadsheet className="w-5 h-5" />
            Exportar Tudo
          </button>
        </div>
      </div>
    </div>
  );
};
